import { HttpErrorResponse, HttpHeaders, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { API_URL } from '@core/config/api-url.token';
import { CORRELATION_ID_HEADER } from '@core/telemetry/telemetry.interceptor';
import { redactUrl } from '@core/telemetry/telemetry-redaction.util';
import { PROVET_DIAGNOSTICS_PATH_PREFIX } from '@shared/constants/api-endpoints.const';
import { generateCorrelationId } from '@shared/utils/id.util';
import { environment } from '@env';
import { DevActionMarkerService } from './dev-action-marker.service';
import { isOwnApiRequest } from './is-own-api-request.util';
import { ProvetRequestCostLogService } from './provet-request-cost-log.service';

export const DEV_ROUTE_HEADER = 'X-Dev-Route';
export const DEV_TRIGGER_HEADER = 'X-Dev-Trigger';
export const PROVET_CALLS_HEADER = 'X-ProVet-Calls';
export const PROVET_CACHE_HITS_HEADER = 'X-ProVet-Cache-Hits';

/**
 * Tags every own-API request with who caused it, and files what the response says that request cost
 * in ProVet traffic, so the diagnostics panel can group a burst of provider calls under the
 * navigation or the action that triggered it.
 *
 * Registered FIRST in `withInterceptors([...])` so the correlation id it mints is already on the
 * request every downstream interceptor sees — one user action, one correlation.
 */
export const devAttributionInterceptor: HttpInterceptorFn = (request, next) => {
  if (!environment.enableDevTools) return next(request);

  const apiUrl = inject(API_URL);
  if (!isOwnApiRequest(request.url, apiUrl)) return next(request);
  if (request.url.includes(PROVET_DIAGNOSTICS_PATH_PREFIX)) return next(request);

  const router = inject(Router);
  const actionMarker = inject(DevActionMarkerService);
  const costLog = inject(ProvetRequestCostLogService);

  const correlationId = generateCorrelationId();
  const route = toHeaderValue(redactUrl(router.url));
  const trigger = actionMarker.currentLabel();

  const setHeaders: Record<string, string> = {
    [CORRELATION_ID_HEADER]: correlationId,
    [DEV_ROUTE_HEADER]: route,
  };
  if (trigger) setHeaders[DEV_TRIGGER_HEADER] = toHeaderValue(trigger);

  const fileCost = (headers: HttpHeaders, status: number): void => {
    const calls = readCount(headers.get(PROVET_CALLS_HEADER));
    const cacheHits = readCount(headers.get(PROVET_CACHE_HITS_HEADER));
    // Absent headers mean "no data" -- the backend has diagnostics off, or CORS is not exposing
    // them yet. Filing a zero would read as "this action was free", which is a different claim.
    if (calls === null && cacheHits === null) return;

    costLog.record({
      at: new Date(),
      method: request.method,
      path: redactUrl(request.urlWithParams),
      status,
      calls: calls ?? 0,
      cacheHits: cacheHits ?? 0,
      correlationId,
      route,
      trigger,
    });
  };

  return next(request.clone({ setHeaders })).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) fileCost(event.headers, event.status);
      },
      // A failed request can still have spent provider budget before failing, so its cost counts.
      error: (error: unknown) => {
        if (error instanceof HttpErrorResponse) fileCost(error.headers, error.status);
      },
    }),
  );
};

function readCount(value: string | null): number | null {
  if (value === null) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

// `XMLHttpRequest.setRequestHeader` rejects anything outside Latin-1, and a Spanish action label
// ("recargar facturación") trips it. Diacritics are folded rather than percent-encoded so the value
// stays human-readable in the panel's timeline, which is the whole point of the header.
function toHeaderValue(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // `redactUrl` marks a stripped query value with U+2026. Folding it to "..." before the
    // Latin-1 filter below keeps that marker legible: dropped outright, "?search=…" reaches the
    // panel as "?search=", which reads as an empty search rather than a redacted one.
    .replace(/\u2026/g, '...')
    .replace(/[^\u0020-\u007e]/g, '');
}
